import { ErrorType, HotelStandalonePaxRestrictionPropsDefault } from '../models';

export const HotelRoomUtils = {
    getModelRoom(adults = 0, kids = [], index = 0) {
        return {
            id: Date.now() + index,
            adults,
            kids,
        };
    },
    getDefaultRoom(roomConfig) {
        return this.getModelRoom(roomConfig.defaultAdults);
    },
    hasEmptyRoom(rooms) {
        return !Array.isArray(rooms) || rooms.length < 1;
    },
    getTotalPax(rooms) {
        if (this.hasEmptyRoom(rooms)) {
            return 2;
        }
        return rooms.reduce((pax, val) => {
            return pax + val.adults + val.kids?.length;
        }, 0);
    },

    getRoomPaxRanges(room, roomConfig, numPax) {
        const {
            maxPassengersPerRoom,
            maxPassengers,
            maxKidsPerAdult,
        } = roomConfig;
        const kids = room.kids.length;
        const adults = room.adults;
        const paxAvailable = maxPassengers - numPax <= 0;
        let maxAdults = 0;
        let maxKidsPerRoom = 0;
        if (paxAvailable) {
            maxAdults = adults;
            maxKidsPerRoom = kids;
        } else {
            maxAdults = maxPassengersPerRoom - kids;
            maxKidsPerRoom = Math.min(
                maxPassengersPerRoom - adults,
                maxKidsPerAdult,
            );
        }
        return {
            maxAdults: maxAdults,
            maxKids: maxKidsPerRoom,
            minAdults: Math.max(Math.round(kids / maxKidsPerAdult), 1),
            minKids: 0,
        };
    },
    getKidsRange(roomConfig) {
        const { maxKidsAge } = roomConfig;
        return Array.from(Array(maxKidsAge + 1).keys());
    },
    getNewRoom(rooms, roomConfig) {
        if (this.hasEmptyRoom(rooms)) {
            return this.getDefaultRoom(roomConfig);
        }
        const { maxPassengers, minAdults, defaultAdults } = roomConfig;
        const pax = this.getTotalPax(rooms);
        const paxAvailable = maxPassengers - pax;
        const adults = paxAvailable <= minAdults ? minAdults : defaultAdults;

        return this.getModelRoom(adults);
    },
    isKidsAgeInvalid(room) {
        const kidsAgeRange = HotelRoomUtils.getKidsRange(HotelStandalonePaxRestrictionPropsDefault);
        const minKidsAge = kidsAgeRange[0] ?? 0;
        const maxKidsAge = kidsAgeRange[kidsAgeRange.length - 1] ?? 17;
        return !room
            || !Array.isArray(room.kids)
            || room.kids.some((age) => typeof age !== 'number'
            || Number.isNaN(age)
            || age < minKidsAge
            || age > maxKidsAge);
    },
    getAgeKidsValidation(rooms) {
        if (this.hasEmptyRoom(rooms)) {
            return null;
        }
        const type = ErrorType.ROOM;
        const index = 0;
        const isValidKidsAges =
            rooms.find((room) => this.isKidsAgeInvalid(room)) === undefined;
        if (!isValidKidsAges) {
            return { index, type, error: 'kidAge' };
        }
        return null;
    },
    getTotalAdultsAndKids(rooms, roomConfig) {
        const availRooms = this.hasEmptyRoom(rooms) ? [this.getDefaultRoom(roomConfig)] : rooms;
        return availRooms.reduce(
            (pax, val) => {
                pax.adults += val.adults;
                pax.kids += val.kids.length;
                return pax;
            },
            { adults: 0, kids: 0 },
        );
    },
    hasAvailableRoomLimitation(rooms, restrictions) {
        return rooms.length < restrictions.maxRooms
            && this.getTotalPax(rooms) < restrictions.maxPassengers;
    },
    restrictMaxRooms(rooms, restrictions) {
        return !this.hasAvailableRoomLimitation(rooms, restrictions)
            ? rooms.slice(0, restrictions.maxRooms)
            : rooms;
    },
    restrictMaxGuests(rooms, restrictions) {
        return rooms.map((room ) => {
            const restrictedRoom = { ...room };
            if (restrictedRoom.kids?.length > restrictions.maxKidsPerAdult) {
                restrictedRoom.kids = restrictedRoom.kids.slice(0, restrictions.maxKidsPerAdult);
            }
            if (restrictedRoom.adults + restrictedRoom.kids?.length > restrictions.maxPassengersPerRoom ) {
                restrictedRoom.adults = restrictions.maxPassengersPerRoom - restrictedRoom.kids.length;
            }
            return restrictedRoom;
        });
    },
    getRoomsAvailableByRestrictions(rooms, restrictions) {
        return this.restrictMaxGuests(this.restrictMaxRooms(rooms, restrictions), restrictions);
    }
};
